import * as React from 'react';

import { Panel, PanelProps } from 'src/components/panelStack/PanelStack';
import { PanelStackDrawer } from 'src/components/resourceDialog/PanelStackDrawer';
import { TEST_IDS } from 'src/constants/testIds';
import {
  ProjectResourceDialogContext,
  useProjectResourceDialogContextValue,
} from 'src/containers/ProjectNewResourceDialogV2/context/useProjectResourceDialog';
import { FileUploadPanelProps } from 'src/containers/ProjectNewResourceDialogV2/panels/FileUploadPanel';
import {
  ResourceTypeSelection,
  ResourceTypeSelectionProps,
} from 'src/containers/ProjectNewResourceDialogV2/panels/ResourceTypeSelection';
import { ProjectNewResourceDialogFooter } from 'src/containers/ProjectNewResourceDialogV2/ProjectNewResourceDialogFooter';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type ProjectNewResourceDialogPanelProps =
  | ResourceTypeSelectionProps
  | FileUploadPanelProps;

export type ProjectNewResourceDialogProps = {
  initialPanelComponent?: React.VFC<
    PanelProps<ProjectNewResourceDialogPanelProps>
  >;
};

export const ProjectNewResourceDialog: React.VFC<
  ProjectNewResourceDialogProps
> = ({ initialPanelComponent }) => {
  const dispatch = useReduxDispatch();
  const contextValue = useProjectResourceDialogContextValue();

  const isOpen = useReduxSelector(
    (state) => state.ui.isProjectResourceDialogOpen
  );

  const wasOpenRef = React.useRef(isOpen);

  React.useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (isOpen && !wasOpen) {
      contextValue.resetDialogState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const initialPanel: Panel<object> = React.useMemo(() => {
    const component = initialPanelComponent || ResourceTypeSelection;
    return {
      renderPanel: component as (
        props: PanelProps<object>
      ) => React.ReactElement | null,
      props: {},
    };
  }, [initialPanelComponent]);

  const handleClose = () => {
    contextValue.resetDialogState();
    dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
  };

  const hasFooterOptions = Boolean(
    contextValue.footerOptions.onBack ||
      contextValue.footerOptions.onNext ||
      contextValue.footerOptions.onCancel ||
      contextValue.footerOptions.onCreate ||
      contextValue.footerOptions.onCreateAndMap
  );

  return (
    <PanelStackDrawer
      ContextProvider={ProjectResourceDialogContext.Provider}
      contextValue={contextValue}
      FooterComponent={ProjectNewResourceDialogFooter}
      fullSize={contextValue.fullSize}
      hasFooterOptions={hasFooterOptions}
      headerBackButton={contextValue.headerBackButton}
      headerBackButtonTestId={TEST_IDS.projectResourceDialog.backButton}
      initialPanel={initialPanel}
      isCloseButtonShown={!contextValue.fullSize}
      isOpen={isOpen}
      onClose={handleClose}
      title={contextValue.dialogTitle}
    />
  );
};
